import React from "react";
import { createStyles, Divider } from "@mantine/core";
import { Button, tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { Link, useOutletContext } from "react-router-dom";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { getRoutePath } from "../../../hooks/useNavigateRoute";
import { formatErrorDetail } from "../../../utils/query.util";
import { formatUtcCalendarDay } from "../../../utils/date.util";
import { Body } from "../../../components/Typography";
import { DetailField } from "../../../components/DetailField";
import { CopyableRow } from "../../../components/CopyableRow";
import { PaymentCard } from "./order-details/PaymentCard";
import { RefundCard } from "./order-details/RefundCard";
import {
  flexColumn,
  formatOrderDate,
  formatUosDateTime,
  linkStyle,
  OrderViewOutletContext,
} from "./order-details/shared";
import { EXPECTED_SHIP_DATE_TOOLTIP } from "./orderTabsCopy";
import { SkeletonField } from "../../../components/skeleton/SkeletonField";
import { SkeletonCard } from "../../../components/skeleton/SkeletonCard";

const useStyles = createStyles(() => ({
  container: {
    ...flexColumn,
    gap: tokens.spacing[6],
    padding: tokens.spacing[4],
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: tokens.spacing[6],
    paddingLeft: tokens.spacing[4],
    paddingRight: tokens.spacing[4],
  },
  cardsRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: tokens.spacing[6],
    "@media (max-width: 62em)": {
      gridTemplateColumns: "1fr",
    },
  },
  link: linkStyle,
  monoId: {
    fontFamily: tokens.font.family.mono,
  },
  errorContainer: {
    ...flexColumn,
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacing[10],
    gap: tokens.spacing[4],
  },
  subtleText: {
    color: tokens.color.semantic.text.subtle,
  },
}));

const OrderDetailsSkeleton: React.FC = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.container}>
      <div className={classes.row}>
        <SkeletonField />
      </div>
      <div className={classes.row}>
        <SkeletonField />
        <SkeletonField />
      </div>
      <Divider />
      <div className={classes.cardsRow}>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    </div>
  );
};

const ReprintParentLink: React.FC<{
  parentCustomerPublicId: string;
  parentCustomerOrderId: string;
  isSandbox: boolean;
}> = ({ parentCustomerPublicId, parentCustomerOrderId, isSandbox }) => {
  const { classes, cx } = useStyles();

  // Look the parent up to show its public Order ID; fall back to the customer order id
  // while loading or when the parent has no public id (mirrors the order header title).
  const { data } = GraphQL.useOrderMinimalQuery({
    orderIdentifier: { customerPublicId: parentCustomerPublicId, customerOrderId: parentCustomerOrderId },
    isSandbox,
  });
  const parentOrderId = data?.orderByIdentifier?.orderPublicId ?? parentCustomerOrderId;

  return (
    <CopyableRow value={parentOrderId} overflow="none" size="base" copyTestId="order-details-reprint-parent-copy">
      <Body>
        Reprint of Order ID{" "}
        <Link
          to={getRoutePath("order:details", {
            customerPublicId: parentCustomerPublicId,
            customerOrderId: parentCustomerOrderId,
          })}
          className={cx(classes.link, classes.monoId)}
          data-testid="order-details-reprint-parent-link"
        >
          {parentOrderId}
        </Link>
      </Body>
    </CopyableRow>
  );
};

export const OrderDetailsTab: React.FC = () => {
  const { customerPublicId, customerOrderId } = useOutletContext<OrderViewOutletContext>();
  const user = useAuthUser();
  const { classes } = useStyles();

  const { data, isLoading, error, refetch } = GraphQL.useOrderDetailsQuery({
    orderIdentifier: {
      customerPublicId,
      customerOrderId,
    },
    isSandbox: user.isSandbox,
  });

  const order = data?.orderByIdentifier;

  if (isLoading) {
    return <OrderDetailsSkeleton />;
  }

  if (error || !order) {
    const errorDetail = formatErrorDetail(error);
    return (
      <div className={classes.errorContainer}>
        <Body>Failed to load order details.</Body>
        {errorDetail && (
          <Body size="sm" className={classes.subtleText}>
            {errorDetail}
          </Body>
        )}
        <Button variant="outlined" intent="neutral" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="order-details-loaded" className={classes.container}>
      <div className={classes.row}>
        <DetailField label="Order Type" testId="order-details-order-type">
          {order.reprintParentCustomerPublicId && order.reprintParentCustomerOrderId ? (
            <ReprintParentLink
              parentCustomerPublicId={order.reprintParentCustomerPublicId}
              parentCustomerOrderId={order.reprintParentCustomerOrderId}
              isSandbox={user.isSandbox}
            />
          ) : (
            "Original"
          )}
        </DetailField>
        <DetailField label="Last Status Message" testId="order-details-last-status-message">
          {order.lastStatusMessage?.trim() || "—"}
        </DetailField>
      </div>

      <div className={classes.row}>
        <DetailField label="Order Date">{order.createdAt ? formatOrderDate(order.createdAt) : "—"}</DetailField>
        {user.is.SUPER_ADMIN && (
          <DetailField label="Organization">
            <Link to={getRoutePath("customer", { publicId: customerPublicId })} className={classes.link}>
              {order.customer?.organizationName ?? customerPublicId}
            </Link>
          </DetailField>
        )}
      </div>

      <div className={classes.row}>
        <DetailField
          label="Expected Ship Date"
          tooltip={EXPECTED_SHIP_DATE_TOOLTIP}
          testId="order-details-expected-ship-date"
        >
          {formatUtcCalendarDay(order.destination?.expectedShipDate, "—")}
        </DetailField>
      </div>

      {user.is.SUPER_ADMIN && order.submittedAt && (
        <div className={classes.row}>
          <DetailField label="Submitted to UOS" testId="order-details-uos-submitted">
            {formatUosDateTime(order.submittedAt)}
          </DetailField>
        </div>
      )}

      <Divider />

      <div className={classes.cardsRow}>
        <PaymentCard order={order} isSandbox={user.isSandbox} />
        {!user.isSandbox && <RefundCard order={order} />}
      </div>
    </div>
  );
};
